import React, { useState, useEffect, useRef, memo } from 'react';
import { FiPhone } from 'react-icons/fi';

// Renders a single remote participant's audio/video tile for an active call.
//
// `peer` is a simple-peer instance created once (in VideoCall's createPeer /
// addPeer) and stored in the peers array under a stable peerID key. Its
// object identity does not change for the lifetime of that connection - it
// is only ever replaced by a brand new peer object under a brand new
// peerID/key when someone rejoins. `index` and `callType` are plain
// primitives. Because of that, React.memo's default shallow prop comparison
// already does the right thing here (skips re-render when peer/index/
// callType are unchanged, re-renders when the participant's position in the
// grid legitimately changes). No custom comparator is needed - a remote
// stream update arrives via the peer's own 'stream' event inside the effect
// below, not via a new peer prop reference, so memo does not risk stale UI.
function VideoPlayer({ peer, index, callType }) {
  const ref = useRef();
  const audioRef = useRef(); // Separate ref for audio element
  const [hasStream, setHasStream] = useState(false);
  const [remoteStream, setRemoteStream] = useState(null);

  useEffect(() => {
    const videoElement = ref.current;
    const audioElement = audioRef.current;

    const handleStream = (stream) => {
      console.log('VideoPlayer received stream, callType:', callType);
      console.log('Stream ID:', stream.id);
      console.log('Stream has audio tracks:', stream.getAudioTracks().length);
      console.log('Stream has video tracks:', stream.getVideoTracks().length);

      // Log track details
      stream.getTracks().forEach(track => {
        console.log(`Track: ${track.kind}, enabled: ${track.enabled}, readyState: ${track.readyState}`);
      });

      setRemoteStream(stream);
      setHasStream(true);

      if (callType === 'audio' && audioElement) {
        // For audio calls, use audio element
        audioElement.srcObject = stream;
        console.log('✅ Audio stream set to audio element');

        // Force play to overcome autoplay policies
        audioElement.play().catch(err => {
          console.error('Error playing audio:', err);
        });
      } else if (callType === 'video' && videoElement) {
        // For video calls, use video element
        videoElement.srcObject = stream;
        console.log('✅ Video stream set to video element');

        // Force play to overcome autoplay policies
        videoElement.play().catch(err => {
          console.error('Error playing video:', err);
        });
      }
    };

    peer.on('stream', handleStream);

    // Check if stream already exists (race condition)
    if (peer.streams && peer.streams.length > 0) {
      console.log('Stream already exists on peer, setting it now');
      handleStream(peer.streams[0]);
    }

    return () => {
      if (videoElement && videoElement.srcObject) {
        videoElement.srcObject.getTracks().forEach(track => track.stop());
        videoElement.srcObject = null;
      }
      if (audioElement && audioElement.srcObject) {
        audioElement.srcObject.getTracks().forEach(track => track.stop());
        audioElement.srcObject = null;
      }
    };
  }, [peer, callType]);

  if (callType === 'audio') {
    return (
      <div className="audio-participant">
        {/* Audio element to play the remote audio - NOT muted so we can hear */}
        <audio
          ref={audioRef}
          autoPlay
          playsInline
        />
        <div className="audio-avatar">
          <FiPhone size={48} />
          <p>Participant {index + 1}</p>
          {hasStream && (
            <p style={{ fontSize: '0.8rem', color: '#2eb67d', marginTop: '10px' }}>
              🔊 Connected
            </p>
          )}
          {!hasStream && (
            <p className="connecting-text" style={{ fontSize: '0.8rem', marginTop: '10px' }}>
              <span className="connecting-dot" aria-hidden="true" />
              Connecting...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="video-container">
      <video
        ref={ref}
        autoPlay
        playsInline
        className="video-element"
      />
      <div className="video-label">Participant {index + 1}</div>
      {!hasStream && (
        <div className="video-loading">
          <p className="connecting-text">
            <span className="connecting-dot" aria-hidden="true" />
            Connecting...
          </p>
        </div>
      )}
      {hasStream && remoteStream && (
        <div className="stream-info">
          <small style={{ fontSize: '0.7rem', color: '#2eb67d' }}>
            🟢 Live
          </small>
        </div>
      )}
    </div>
  );
}

export default memo(VideoPlayer);
